import {
  DEFAULT_EXPOSURE_AREA_RATE,
  DEFAULT_EXPOSURE_PLUGIN_URL,
  DEFAULT_EXPOSURE_REPEATED,
  DEFAULT_EXPOSURE_STAY_DURATION,
} from "./constants";
import type {
  ExposureConfig,
  ExposureTarget,
  RegisterExposureOptions,
  SensorsExposureInstance,
  SensorsExposurePlugin,
  SensorsSDK,
} from "./types";

let exposurePluginPromise: Promise<SensorsExposurePlugin> | null = null;

export function loadExposurePlugin(pluginUrl: string = DEFAULT_EXPOSURE_PLUGIN_URL): Promise<SensorsExposurePlugin> {
  if (!exposurePluginPromise) {
    let dynamicImport: (url: string) => Promise<Record<string, any>>;

    try {
      dynamicImport = new Function("url", "return import(url);") as (url: string) => Promise<Record<string, any>>;
    } catch (error) {
      return Promise.reject(error);
    }

    exposurePluginPromise = dynamicImport(pluginUrl).then((module) => {
      const plugin = module.default || module.Exposure || module;

      if (!plugin) {
        throw new Error(`Sensors Exposure plugin loaded, but no plugin export was found: ${pluginUrl}`);
      }

      return plugin as SensorsExposurePlugin;
    }).catch((error) => {
      exposurePluginPromise = null;
      throw error;
    });
  }

  return exposurePluginPromise;
}

export function useExposurePlugin(
  sensors: SensorsSDK,
  config: ExposureConfig,
  pluginUrl?: string,
): Promise<SensorsExposureInstance> {
  if (!sensors.use) {
    return Promise.reject(new Error("Sensors SDK does not support sensors.use."));
  }

  return loadExposurePlugin(pluginUrl).then((Exposure) => {
    const exposure = sensors.use?.(Exposure, normalizeExposureConfig(config)) as SensorsExposureInstance | undefined;

    if (!exposure) {
      throw new Error("Sensors Exposure plugin was registered, but no exposure instance was returned.");
    }

    return exposure;
  });
}

export function resolveElement(target: ExposureTarget): Element | null {
  if (typeof target === "string") {
    try {
      return document.querySelector(target);
    } catch (error) {
      logExposureUtilityError("resolve exposure element", error);
      return null;
    }
  }

  if (typeof Element !== "undefined" && target instanceof Element) {
    return target;
  }

  if (target && typeof target === "object" && "current" in target) {
    return target.current || null;
  }

  return null;
}

export function toSensorsExposureViewOption(
  options: RegisterExposureOptions | undefined,
  element: Element | undefined,
  initConfig: ExposureConfig,
): Record<string, any> {
  const attributeOptions = element ? getElementExposureOptions(element) : {};
  const legacyOptions = options as Record<string, any>;
  const listener = normalizeExposureListener(options?.listener || legacyOptions?.listener);

  return {
    eventName: options?.event_name || legacyOptions?.eventName || attributeOptions.event_name,
    properties: {
      ...(attributeOptions.properties || {}),
      ...(options?.properties || {}),
    },
    config: normalizeExposureConfig({
      ...initConfig,
      ...(attributeOptions.config || {}),
      ...(options?.config || legacyOptions?.config || {}),
    }),
    listener: listener
      ? {
          shouldExpose: listener.should_expose,
          didExpose: listener.did_expose,
        }
      : undefined,
  };
}

function getElementExposureOptions(element: Element): RegisterExposureOptions {
  const optionText = element.getAttribute("data-sensors-exposure-option");
  const options = parseJsonAttribute<Record<string, any>>(optionText) || {};
  const eventName = element.getAttribute("data-sensors-exposure-event-name");

  return {
    event_name: eventName || options.event_name || options.eventName,
    properties: {
      ...(options.properties || {}),
      ...getElementExposureProperties(element),
    },
    config: {
      ...(normalizeExposureConfig(options.config, {}) || {}),
      ...getElementExposureConfig(element),
    },
  };
}

function getElementExposureConfig(element: Element): ExposureConfig {
  const areaRate = parseNumberAttribute(element.getAttribute("data-sensors-exposure-config-area_rate"));
  const stayDuration = parseNumberAttribute(element.getAttribute("data-sensors-exposure-config-stay_duration"));
  const repeated = parseBooleanAttribute(element.getAttribute("data-sensors-exposure-config-repeated"));

  return {
    ...(typeof areaRate === "number" ? { area_rate: areaRate } : {}),
    ...(typeof stayDuration === "number" ? { stay_duration: stayDuration } : {}),
    ...(typeof repeated === "boolean" ? { repeated } : {}),
  };
}

function getElementExposureProperties(element: Element): Record<string, string> {
  const properties: Record<string, string> = {};
  const prefix = "data-sensors-exposure-property-";

  for (let i = 0; i < element.attributes.length; i++) {
    const attribute = element.attributes[i];

    if (attribute.name.indexOf(prefix) === 0) {
      properties[attribute.name.slice(prefix.length)] = attribute.value;
    }
  }

  return properties;
}

export function normalizeExposureConfig(
  config?: Record<string, any>,
  defaults: ExposureConfig = {
    area_rate: DEFAULT_EXPOSURE_AREA_RATE,
    stay_duration: DEFAULT_EXPOSURE_STAY_DURATION,
    repeated: DEFAULT_EXPOSURE_REPEATED,
  },
): ExposureConfig {
  const areaRate = config?.area_rate ?? config?.areaRate ?? defaults.area_rate;
  const stayDuration = config?.stay_duration ?? config?.stayDuration ?? defaults.stay_duration;
  const repeated = config?.repeated ?? defaults.repeated;

  return {
    ...(typeof areaRate === "number" ? { area_rate: areaRate } : {}),
    ...(typeof stayDuration === "number" ? { stay_duration: stayDuration } : {}),
    ...(typeof repeated === "boolean" ? { repeated } : {}),
  };
}

function normalizeExposureListener(listener?: Record<string, any>): RegisterExposureOptions["listener"] | undefined {
  if (!listener) {
    return undefined;
  }

  return {
    should_expose: listener.should_expose || listener.shouldExpose,
    did_expose: listener.did_expose || listener.didExpose,
  };
}

function parseNumberAttribute(value: string | null): number | undefined {
  if (value === null || value === "") {
    return undefined;
  }

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? undefined : numberValue;
}

function parseBooleanAttribute(value: string | null): boolean | undefined {
  if (value === null || value === "") {
    return undefined;
  }

  return value === "true";
}

function parseJsonAttribute<T>(value: string | null): T | undefined {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    return undefined;
  }
}

export function noop(): void {
  return undefined;
}

function logExposureUtilityError(context: string, error: unknown): void {
  if (typeof console !== "undefined" && console.error) {
    console.error(`[GatCollect] ${context} error:`, error);
  }
}
